import { useMemo, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import NavGroup from './NavGroup';
import menuItem from './../../../../menu-items';
import { useGetMenuMaster } from './../../../../api/menu';
import { NavKeyboardCtx } from './NavKeyboardContext';

export default function Navigation() {
  const navigate = useNavigate();
  const baseItems = menuItem?.items || [];

  const [input, setInput] = useState('');
  const [query, setQuery] = useState('');

  // ── keyboard-nav state
  const [kbFocusIdx, setKbFocusIdx] = useState(-1); // index into flatNavItems
  const [kbOpenIds, setKbOpenIds] = useState(new Set()); // collapses opened via kb

  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;

  useEffect(() => {
    const id = setTimeout(() => setQuery(input.trim()), 300);
    return () => clearTimeout(id);
  }, [input]);

  // Reset kb state whenever the search query changes
  useEffect(() => {
    setKbFocusIdx(-1);
    setKbOpenIds(new Set());
  }, [query]);

  const matches = (text, q) => {
    if (!q) return true;
    if (!text) return false;
    return text.toLowerCase().includes(q.toLowerCase());
  };

  const filteredItems = useMemo(() => {
    if (!query) return baseItems;
    const out = [];
    for (const group of baseItems) {
      const children = group.children || [];
      if (matches(group.title || group.id || '', query)) {
        out.push(group);
        continue;
      }
      const newChildren = [];
      for (const child of children) {
        if (child.type === 'collapse' && Array.isArray(child.children)) {
          if (matches(child.title || child.id || '', query)) {
            newChildren.push(child);
            continue;
          }
          const filteredSub = child.children.filter((sub) => matches(sub.title || sub.id || '', query));
          if (filteredSub.length) {
            newChildren.push({ ...child, autoOpen: true, children: filteredSub });
          }
        } else {
          if (matches(child.title || child.id || '', query)) {
            newChildren.push(child);
          }
        }
      }
      if (newChildren.length) {
        out.push({ ...group, children: newChildren });
      }
    }
    return out;
  }, [baseItems, query]);

  // ── Flat ordered list for keyboard navigation
  // Includes children of collapses that are autoOpen OR keyboard-opened.
  const flatNavItems = useMemo(() => {
    const flat = [];
    for (const group of filteredItems) {
      for (const child of group.children || []) {
        if (child.type === 'item') {
          flat.push(child);
        } else if (child.type === 'collapse') {
          flat.push(child);
          if (kbOpenIds.has(child.id) || child.autoOpen) {
            for (const sub of child.children || []) {
              if (sub.type === 'item') flat.push(sub);
            }
          }
        }
      }
    }
    return flat;
  }, [filteredItems, kbOpenIds]);

  // Safely derive the focused item's id (null when out of bounds)
  const kbFocusedId = kbFocusIdx >= 0 && kbFocusIdx < flatNavItems.length ? (flatNavItems[kbFocusIdx]?.id ?? null) : null;

  const handleSearchKeyDown = useCallback(
    (e) => {
      if (flatNavItems.length === 0) return;

      switch (e.key) {
        case 'ArrowDown': {
          e.preventDefault();
          setKbFocusIdx((prev) => (prev < flatNavItems.length - 1 ? prev + 1 : 0));
          break;
        }
        case 'ArrowUp': {
          e.preventDefault();
          setKbFocusIdx((prev) => (prev > 0 ? prev - 1 : flatNavItems.length - 1));
          break;
        }
        case 'ArrowRight':
        case 'Enter': {
          const focused = kbFocusIdx >= 0 ? flatNavItems[kbFocusIdx] : null;
          if (!focused) {
            // Nothing focused yet → focus first item
            setKbFocusIdx(0);
            break;
          }
          if (focused.type === 'collapse') {
            e.preventDefault();
            // Open if not already open (autoOpen items are already open)
            if (!kbOpenIds.has(focused.id) && !focused.autoOpen) {
              setKbOpenIds((prev) => new Set([...prev, focused.id]));
            }
            // Move to first child (it will be at prev+1 after the re-render)
            if ((focused.children?.length ?? 0) > 0) {
              setKbFocusIdx((prev) => prev + 1);
            }
          } else if (e.key === 'Enter' && focused.url) {
            e.preventDefault();
            navigate(focused.url);
            // Clear search after navigation
            setInput('');
            setQuery('');
            setKbFocusIdx(-1);
            setKbOpenIds(new Set());
          }
          break;
        }
        case 'ArrowLeft': {
          const focused = kbFocusIdx >= 0 ? flatNavItems[kbFocusIdx] : null;
          if (!focused) break;

          if (focused.type === 'collapse' && kbOpenIds.has(focused.id)) {
            // Collapse is kb-open → close it
            e.preventDefault();
            setKbOpenIds((prev) => {
              const next = new Set(prev);
              next.delete(focused.id);
              return next;
            });
          } else {
            // Item (or non-kb-opened collapse) → jump to parent collapse
            for (let i = kbFocusIdx - 1; i >= 0; i--) {
              const candidate = flatNavItems[i];
              if (candidate?.type === 'collapse' && candidate.children?.some((c) => c.id === focused.id)) {
                e.preventDefault();
                setKbFocusIdx(i);
                break;
              }
            }
          }
          break;
        }
        case 'Escape': {
          setKbFocusIdx(-1);
          break;
        }
        default:
          break;
      }
    },
    [flatNavItems, kbFocusIdx, kbOpenIds, navigate]
  );

  // Memoised context value — only changes when focused id or open-set changes
  const ctxValue = useMemo(() => ({ kbFocusedId, kbOpenIds }), [kbFocusedId, kbOpenIds]);

  const navGroups = filteredItems.map((item) => <NavGroup key={item.id} item={item} />);

  return (
    <NavKeyboardCtx.Provider value={ctxValue}>
      {' '}
      <Box sx={{ display: 'flex', flexDirection: 'column', pt: 1 }}>
        {drawerOpen && (
          <Box sx={{ px: 1.5, pb: 1 }}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              placeholder="Search menu..."
              aria-label="Search menu"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 15, color: 'var(--appMain300)' }} />
                  </InputAdornment>
                ),
                endAdornment: input ? (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="Clear search"
                      edge="end"
                      size="small"
                      onClick={() => {
                        setInput('');
                        setQuery('');
                        setKbFocusIdx(-1);
                        setKbOpenIds(new Set());
                      }}
                      sx={{ color: 'var(--appMain300)', '&:hover': { color: 'var(--appMain600)' } }}
                    >
                      <ClearIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </InputAdornment>
                ) : null
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  bgcolor: '#f5f8fd',
                  fontSize: 13,
                  transition: 'box-shadow 0.15s',
                  '& fieldset': { borderColor: 'var(--appMain100)' },
                  '&:hover fieldset': { borderColor: 'var(--appMain300)' },
                  '&.Mui-focused fieldset': { borderColor: 'var(--appMain400)', borderWidth: '1px' },
                  '&.Mui-focused': { boxShadow: '0 0 0 3px rgba(36,58,110,0.07)' }
                },
                '& .MuiOutlinedInput-input': {
                  color: 'var(--appMain900)',
                  '&::placeholder': { color: 'var(--appMain200)', opacity: 1 }
                }
              }}
            />
          </Box>
        )}

        <Box sx={{ pt: 0.5 }}>{navGroups}</Box>

        {filteredItems.length === 0 && query && (
          <Box sx={{ px: 3, py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75 }}>
            <SearchIcon sx={{ fontSize: 26, color: 'var(--appMain200)' }} />
            <Typography sx={{ fontSize: 12.5, fontWeight: 500, color: 'var(--appMain400)', textAlign: 'center' }}>
              No results for "{query}"
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: 'var(--appMain300)', textAlign: 'center' }}>Try a different keyword</Typography>
          </Box>
        )}
      </Box>
    </NavKeyboardCtx.Provider>
  );
}
